/**
 * Supabase Auth half of AccountProvider (web2 migration, step 1). Supabase proves who the
 * player is (email code / magic link / OAuth); POST /auth/supabase turns that proof into
 * the same account session a wallet login produces. Nothing downstream knows the
 * difference — identity stays keyed on account_id.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { exchangeSupabaseSession } from '../api/authApi';
import { supabase, isSupabaseAuthEnabled } from './supabaseClient';
import type { AccountSession } from './sessionStore';
import type { OAuthProvider } from './useAccountId';
import { translate } from "../i18n/translate";

interface SupabaseLoginDeps {
  /** The current account session (read at event time, so passed as a ref). */
  sessionRef: { readonly current: AccountSession | null };
  persist: (s: AccountSession) => void;
  onNewAccount: () => void;
  onSignedOut: () => void;
}

function requireSupabase() {
  if (!supabase) throw new Error('Email sign-in is not available right now.');
  return supabase;
}

/** Parameters Supabase appends to the return URL of a magic link or OAuth redirect. */
const CALLBACK_PARAMS = ['code', 'error', 'error_code', 'error_description'] as const;

/** Where magic links and OAuth providers send the player back to: the page they started on. */
function currentPageUrl(): string {
  const url = new URL(window.location.href);
  url.hash = '';
  for (const key of CALLBACK_PARAMS) url.searchParams.delete(key);
  return url.toString();
}

/** The Supabase `error_code` of a failed redirect, from the query or the hash, if there is one. */
function redirectErrorCode(): string | null {
  const url = new URL(window.location.href);
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
  return url.searchParams.get('error_code') ?? hash.get('error_code');
}

/**
 * Drop a failed redirect's parameters from the address bar, so a reload or a shared link does
 * not replay the error. The rest of the URL (path, other query keys) is kept.
 */
function clearRedirectParams(): void {
  const cleaned = currentPageUrl();
  if (cleaned !== window.location.href) window.history.replaceState(window.history.state, '', cleaned);
}

/** Supabase's `error_code` for an email link that is too old or already used. */
const EXPIRED_LINK_CODE = 'otp_expired';

/**
 * What the player reads when a redirect came back failed. The provider's raw text
 * ("Unable to exchange external code: 4/0A…") means nothing to a player; it goes to the console.
 */
function redirectFailureMessage(code: string | null): string {
  return code === EXPIRED_LINK_CODE
    ? translate('account.signInError.expired')
    : translate('account.signInError.unfinished');
}

/**
 * A failed sign-in, as the notice shows it. `retryable` is true only when a retry can succeed
 * on its own — the Supabase session exists and only our backend exchange failed. A failed
 * redirect needs the player to start again.
 */
export interface LoginFailure {
  message: string;
  retryable: boolean;
}

/**
 * Whether a Supabase session's ACCESS token has already expired. supabase-js hands a restored
 * session to INITIAL_SESSION before refreshing it, so a tab that slept past the token's hour
 * delivers a dead token first and a fresh one (TOKEN_REFRESHED) moments later — or SIGNED_OUT
 * when the refresh token is gone too. Exchanging the dead one is refused by the backend
 * ("Invalid or expired Supabase session") and used to put a sign-in error on screen over a
 * player whose game session was perfectly good.
 */
function accessTokenExpired(sbSession: Session): boolean {
  return typeof sbSession.expires_at === 'number' && sbSession.expires_at * MS_PER_SECOND <= Date.now();
}

const MS_PER_SECOND = 1000;

/**
 * A failed account exchange that completeLogin has ALREADY dealt with — shown to the player, or
 * deliberately kept quiet for a background re-check. Callers that await it (restoreSession)
 * must not report it a second time. Keeps the original message, so the email-code form still
 * shows the real reason.
 */
class ExchangeError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'ExchangeError';
  }
}

/** The Supabase session is fine but turning it into an account session failed: a retry can work. */
function exchangeFailed(error: unknown): LoginFailure {
  return { message: error instanceof Error ? error.message : translate('account.signInError.failed'), retryable: true };
}

export function useSupabaseLogin({ sessionRef, persist, onNewAccount, onSignedOut }: SupabaseLoginDeps) {
  // One exchange per Supabase access token: the explicit verifyEmailCode call and the
  // SIGNED_IN event it triggers must not mint two account sessions.
  const inFlight = useRef<{ accessToken: string; promise: Promise<void> } | null>(null);
  const generation = useRef(0);
  const logoutVersion = useRef(0);
  const [loginError, setLoginError] = useState<LoginFailure | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const completeLogin = useCallback(
    (sbSession: Session): Promise<void> => {
      if (inFlight.current?.accessToken === sbSession.access_token) return inFlight.current.promise;
      const attempt = ++generation.current;
      // Re-checking the player already signed in (a refreshed token, a restored tab) happens in
      // the background: no "Finishing sign-in…", and a failure is not a sign-in failure — their
      // game session still stands and the next refresh tries again.
      const revalidating = sessionRef.current?.chain === 'supabase'
        && sessionRef.current.authUserId === sbSession.user.id;
      setLoginError(null);
      if (!revalidating) setIsSigningIn(true);
      const promise = exchangeSupabaseSession(sbSession.access_token).then((r) => {
        if (attempt !== generation.current) return;
        // Web2 bridge: the account_id IS the player key the app passes wherever it used to pass
        // a wallet address (see ACCOUNT_KEY_PATTERN in shared/domain/addressNormalize).
        const metadata = sbSession.user.user_metadata;
        const displayName = [metadata?.user_name, metadata?.preferred_username, metadata?.full_name, metadata?.name]
          .find((value): value is string => typeof value === 'string' && value.trim().length > 0) ?? null;
        persist({ token: r.token, accountId: r.account_id, address: r.account_id, chain: 'supabase',
          authUserId: sbSession.user.id, email: sbSession.user.email ?? null, displayName });
        if (r.is_new) onNewAccount();
      }).catch((error: unknown) => {
        if (attempt === generation.current) {
          inFlight.current = null;
          if (revalidating) {
            console.warn('[login] Background session re-check failed (still signed in)', {
              message: error instanceof Error ? error.message : String(error),
            });
          } else {
            setLoginError(exchangeFailed(error));
          }
        }
        throw new ExchangeError(error);
      }).finally(() => {
        if (attempt === generation.current) setIsSigningIn(false);
      });
      inFlight.current = { accessToken: sbSession.access_token, promise };
      return promise;
    },
    [sessionRef, persist, onNewAccount]
  );

  const restoreSession = useCallback(async () => {
    if (!supabase) return;
    const version = logoutVersion.current;
    setLoginError(null);
    const errorCode = redirectErrorCode();
    try {
      // initialize() also reports PKCE/magic-link errors, which auth events alone omit.
      const initialized = await supabase.auth.initialize();
      if (version !== logoutVersion.current) return;
      if (initialized.error) {
        console.error('[login] Sign-in redirect failed', { code: errorCode, message: initialized.error.message });
        clearRedirectParams();
        setLoginError({ message: redirectFailureMessage(errorCode), retryable: false });
        return;
      }
      const { data, error } = await supabase.auth.getSession();
      if (version !== logoutVersion.current) return;
      if (error) throw error;
      // An expired token is left to the refresh that follows it (see accessTokenExpired).
      if (data.session && !accessTokenExpired(data.session)) await completeLogin(data.session);
    } catch (error) {
      if (version !== logoutVersion.current) return;
      // completeLogin has already reported (or deliberately silenced) its own failure.
      if (!(error instanceof ExchangeError)) setLoginError(exchangeFailed(error));
    }
  }, [completeLogin]);

  const dismissLoginError = useCallback(() => setLoginError(null), []);

  useEffect(() => {
    if (!supabase) return;
    const { data } = supabase.auth.onAuthStateChange((event, sbSession) => {
      const current = sessionRef.current;
      if (event === 'SIGNED_OUT') {
        logoutVersion.current++;
        generation.current++;
        inFlight.current = null;
        setIsSigningIn(false);
        if (current?.chain === 'supabase') onSignedOut();
        return;
      }
      if (!sbSession) return;
      // Reconcile restored, refreshed and switched users with the game session. The
      // token cache deduplicates INITIAL_SESSION, SIGNED_IN and explicit OTP completion.
      if (!['INITIAL_SESSION', 'SIGNED_IN', 'TOKEN_REFRESHED'].includes(event)) return;
      // A dead token is not worth an exchange: its TOKEN_REFRESHED (or SIGNED_OUT) is on the way.
      if (accessTokenExpired(sbSession)) return;
      completeLogin(sbSession).catch((error: unknown) => {
        console.error('[login] Supabase session exchange failed', {
          message: error instanceof Error ? error.message : String(error),
        });
      });
    });
    void restoreSession();
    return () => data.subscription.unsubscribe();
  }, [sessionRef, completeLogin, onSignedOut, restoreSession]);

  const sendEmailCode = useCallback(async (email: string) => {
    const { error } = await requireSupabase().auth.signInWithOtp({
      email,
      options: { emailRedirectTo: currentPageUrl(), shouldCreateUser: true },
    });
    if (error) throw new Error(error.message);
  }, []);

  const verifyEmailCode = useCallback(
    async (email: string, code: string) => {
      const { data, error } = await requireSupabase().auth.verifyOtp({ email, token: code, type: 'email' });
      if (error) throw new Error(error.message);
      if (!data.session) throw new Error('That code did not sign you in. Request a new one.');
      await completeLogin(data.session);
    },
    [completeLogin]
  );

  const loginWithOAuth = useCallback(async (provider: OAuthProvider) => {
    const { error } = await requireSupabase().auth.signInWithOAuth({
      provider,
      options: { redirectTo: currentPageUrl() },
    });
    if (error) throw new Error(error.message);
  }, []);

  const signOutSupabase = useCallback(() => {
    logoutVersion.current++;
    generation.current++;
    inFlight.current = null;
    setIsSigningIn(false);
    setLoginError(null);
    supabase?.auth.signOut().catch(() => undefined);
  }, []);

  return {
    supabaseAuthEnabled: isSupabaseAuthEnabled,
    sendEmailCode,
    verifyEmailCode,
    loginWithOAuth,
    signOutSupabase,
    loginError,
    dismissLoginError,
    isSigningIn,
    retryLogin: restoreSession,
  };
}
